import axios from '@/lib/axios'
import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import type { Invoice } from '@/modules/invoices/interfaces/invoice'
import useInvoice, { invoiceKey } from '@/modules/invoices/composables/useInvoice'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { isAxiosError } from 'axios'
import { computed, type MaybeRef } from 'vue'
import { useToast } from 'vue-toastification'

export const createInvoiceDraft = async (requestId: number): Promise<Invoice> => {
  const { data } = await axios.post<{ data: Invoice }>(
    `/api/requests/${requestId}/invoice/draft`,
    {},
    { errorHandling: false } as CustomAxiosRequestConfig,
  )
  return data.data
}

export const stampInvoice = async (requestId: number, invoiceId: number): Promise<Invoice> => {
  const { data } = await axios.post<{ data: Invoice }>(
    `/api/requests/${requestId}/invoice/${invoiceId}/stamp`,
    {},
    { errorHandling: false } as CustomAxiosRequestConfig,
  )
  return data.data
}

export const cancelInvoice = async (
  requestId: number,
  invoiceId: number,
  motive = '02',
): Promise<{ message: string; data: Invoice }> => {
  const { data } = await axios.delete<{ message: string; data: Invoice }>(
    `/api/requests/${requestId}/invoice/${invoiceId}`,
    { data: { motive }, errorHandling: false } as CustomAxiosRequestConfig,
  )
  return data
}

export const updateInvoiceDraft = async (
  requestId: number,
  invoiceId: number,
  payload: Record<string, unknown> = {},
): Promise<Invoice> => {
  const { data } = await axios.put<{ data: Invoice }>(
    `/api/requests/${requestId}/invoice/${invoiceId}/draft`,
    payload,
    { errorHandling: false } as CustomAxiosRequestConfig,
  )
  return data.data
}

export const downloadBlobAs = (blobUrl: string, filename: string): void => {
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const downloadInvoiceXml = async (companyId: number, invoiceId: number, uuid: string): Promise<void> => {
  const response = await axios.get(
    `/api/companies/${companyId}/invoices/${invoiceId}/xml`,
    { responseType: 'blob', errorHandling: false } as CustomAxiosRequestConfig,
  )
  const url = URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }))
  downloadBlobAs(url, `cfdi-${uuid}.xml`)
  URL.revokeObjectURL(url)
}

export const previewInvoice = async (requestId: number, invoiceId: number): Promise<string> => {
  const response = await axios.get(
    `/api/requests/${requestId}/invoice/${invoiceId}/preview`,
    { responseType: 'blob', errorHandling: false } as CustomAxiosRequestConfig,
  )
  return URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
}

const useRequestInvoice = (requestId: number, enabled: MaybeRef<boolean> = true) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { invoice, isLoading } = useInvoice(requestId, enabled)

  const invalidate = () => queryClient.invalidateQueries(invoiceKey(requestId))

  const errorMsg = (err: unknown, fallback: string) => {
    const msg = isAxiosError(err) ? (err.response?.data as { message?: string })?.message : undefined
    return msg ?? fallback
  }

  const createDraftMutation = useMutation(
    () => createInvoiceDraft(requestId),
    {
      onSuccess: () => {
        invalidate()
        toast.success('Borrador de factura creado')
      },
      onError: (err: unknown) => toast.error(errorMsg(err, 'No se pudo crear el borrador')),
    },
  )

  const stampMutation = useMutation(
    (invoiceId: number) => stampInvoice(requestId, invoiceId),
    {
      onSuccess: () => {
        invalidate()
        toast.success('Factura timbrada correctamente')
      },
      onError: (err: unknown) => toast.error(errorMsg(err, 'No se pudo timbrar la factura')),
    },
  )

  const cancelMutation = useMutation(
    ({ invoiceId, motive }: { invoiceId: number; motive: string }) =>
      cancelInvoice(requestId, invoiceId, motive),
    {
      onSuccess: (res) => {
        invalidate()
        toast.success(res.message ?? 'Solicitud de cancelación enviada')
      },
      onError: (err: unknown) => toast.error(errorMsg(err, 'No se pudo cancelar la factura')),
    },
  )

  const previewMutation = useMutation(
    (invoiceId: number) => previewInvoice(requestId, invoiceId),
    {
      onError: (err: unknown) => toast.error(errorMsg(err, 'No se pudo cargar la previsualización')),
    },
  )

  const isActing = computed(
    () =>
      createDraftMutation.isLoading.value ||
      stampMutation.isLoading.value ||
      cancelMutation.isLoading.value ||
      previewMutation.isLoading.value,
  )

  return {
    invoice,
    isLoading,
    isActing,
    createDraft: () => createDraftMutation.mutate(),
    createDraftAsync: () => createDraftMutation.mutateAsync(),
    stamp: (invoiceId: number) => stampMutation.mutate(invoiceId),
    stampAsync: (invoiceId: number) => stampMutation.mutateAsync(invoiceId),
    cancel: (invoiceId: number, motive = '02') => cancelMutation.mutate({ invoiceId, motive }),
    previewAsync: (invoiceId: number) => previewMutation.mutateAsync(invoiceId),
  }
}

export default useRequestInvoice
